import React from 'react';
import Select, { components } from 'react-select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronLeft, faChevronRight, faDownload, faEye, faEyeSlash,
} from '@fortawesome/pro-regular-svg-icons';

import Authorize from '../../../components/Authorize';
import { CONFIG } from '../../../components/Authorize/permissions/resources';
import Arrange from '../../Mapping/Arrange';
import { formatSession } from '../compute';
import { DETECTABLES_SELECT_STYLES, SCAN_SELECT_STYLES } from '../helpers';
import SrsToggle from '../srs/layers';
import cc from '../style.module.scss';
import style from './style.module.scss';

const stopMouseDown = (e) => {
  e.preventDefault();
  e.stopPropagation();
};

/** Closed picker shows session time for regions; menu stays nested under session headers. */
const formatScanLabel = (option, { context }) => {
  if (context === 'value' && option?.kind === 'region' && option.session) {
    return `${formatSession(option.session)} · ${option.label}`;
  }
  return option?.label || '';
};

const Field = ({
  label, children, icon, onIconClick, iconTitle, iconActive,
}) => (
  <div className={cc.field}>
    <span>
      {icon && onIconClick ? (
        <button
          type="button"
          className={[cc.fieldIconBtn, iconActive && cc.fieldIconBtnOn].filter(Boolean).join(' ')}
          onClick={onIconClick}
          title={iconTitle}
          aria-label={iconTitle || label}
          aria-pressed={iconActive}
        >
          <FontAwesomeIcon icon={icon} />
        </button>
      ) : null}
      {label}
    </span>
    {children}
  </div>
);

const DetectablesValueContainer = ({
  getValue, selectProps, children, ...rest
}) => {
  const values = getValue();
  let summary = null;
  if (!selectProps.inputValue) {
    if (values.length === 1) summary = values[0].label;
    else if (values.length > 1) summary = `${values.length} selected`;
  }
  return (
    <components.ValueContainer getValue={getValue} selectProps={selectProps} {...rest}>
      {summary ? <span className={style.valueSummary}>{summary}</span> : null}
      {children}
    </components.ValueContainer>
  );
};

const DetectablesMenuList = ({
  selectProps, children, setValue, clearValue, ...rest
}) => (
  <components.MenuList
    selectProps={selectProps}
    setValue={setValue}
    clearValue={clearValue}
    {...rest}
  >
    <div className={style.menuActions}>
      <button
        type="button"
        disabled={selectProps.isDisabled || !(selectProps.options || []).length}
        onMouseDown={stopMouseDown}
        onClick={() => setValue(selectProps.options, 'select-option')}
      >
        Select all
      </button>
      <button
        type="button"
        disabled={selectProps.isDisabled}
        onMouseDown={stopMouseDown}
        onClick={() => clearValue()}
      >
        Clear all
      </button>
    </div>
    {children}
  </components.MenuList>
);

const detectablesComponents = {
  MultiValue: () => null,
  ValueContainer: DetectablesValueContainer,
  MenuList: DetectablesMenuList,
};

const NavBtn = ({
  delta, onStep, disabled, label,
}) => (
  <button
    type="button"
    className={style.scanNavBtn}
    onClick={() => onStep(delta)}
    disabled={disabled}
    aria-label={label}
    title={label}
  >
    <FontAwesomeIcon icon={delta < 0 ? faChevronLeft : faChevronRight} />
  </button>
);

const Controls = ({ data, actions }) => {
  const { controls, ui, ready } = data;
  const { showBoxes } = ui;

  return (
    <div className={style.controls}>
      <div className={style.scan}>
        <Field label="Scan">
          <div className={style.scanPicker}>
            <NavBtn
              delta={-1}
              onStep={actions.goScan}
              disabled={!controls.canNavigate}
              label="Previous scan"
            />
            <Select
              className={style.ccSelect}
              classNamePrefix="react-select"
              inputId="crowd-count-scan"
              isSearchable={false}
              styles={SCAN_SELECT_STYLES}
              options={controls.scanOptions}
              value={controls.option}
              onChange={actions.setOption}
              formatOptionLabel={formatScanLabel}
              placeholder="No scans"
              isDisabled={!controls.hasScans}
            />
            <NavBtn
              delta={1}
              onStep={actions.goScan}
              disabled={!controls.canNavigate}
              label="Next scan"
            />
          </div>
        </Field>
      </div>

      <div className={style.detectables}>
        <Field
          label="Detectables"
          icon={showBoxes ? faEye : faEyeSlash}
          iconActive={showBoxes}
          iconTitle={showBoxes ? 'Hide detection boxes' : 'Show detection boxes'}
          onIconClick={actions.toggleShowBoxes}
        >
          <Select
            className={style.ccSelect}
            classNamePrefix="react-select"
            inputId="crowd-count-detectables"
            styles={DETECTABLES_SELECT_STYLES}
            components={detectablesComponents}
            isMulti
            isClearable
            isSearchable
            closeMenuOnSelect={false}
            hideSelectedOptions={false}
            options={controls.detectableOptions}
            value={controls.selectedOptions}
            onChange={(next) => actions.setSelectedLabels((next || []).map((o) => o.value))}
            placeholder="Select labels"
            isDisabled={!controls.detectableOptions.length}
          />
        </Field>
      </div>

      <div className={style.srsToggle}>
        <SrsToggle show={ui.srsShow} onToggle={actions.toggleSrsShow} />
      </div>

      {ready ? (
        <button
          type="button"
          className={style.exportBtn}
          onClick={actions.exportAnalysis}
          title="Download analysis JSON"
        >
          <FontAwesomeIcon icon={faDownload} />
          Export
        </button>
      ) : null}

      <Authorize permissions={CONFIG}>
        <Arrange onSaved={actions.reloadLayouts} />
      </Authorize>
    </div>
  );
};

export default Controls;
